import { GOOGLE_MAPS_LIBRARIES } from '../../../../config/googleMaps';

// Map container style
export const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Default center (Houston, TX)
export const defaultCenter = {
  lat: 29.7604,
  lng: -95.3698
};

// Re-export GOOGLE_MAPS_LIBRARIES from shared config for backward compatibility
export { GOOGLE_MAPS_LIBRARIES };

// Dark mode map styles
export const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

// Simplified map styles - hide green areas, parks, and other features
export const simplifiedMapStyles: google.maps.MapTypeStyle[] = [
  // Hide parks and green areas
  {
    featureType: "poi.park",
    elementType: "all",
    stylers: [{ visibility: "off" }]
  },
  // Hide other points of interest
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }]
  },
  // Hide transit stations
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }]
  },
  // Simplify landscape (remove green coloring)
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }]
  },
  // Keep roads visible with clear styling
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#d0d0d0" }]
  }
];

// Map options - will be updated with dark mode
// Map ID is required for AdvancedMarkerElement
// Note: When using mapId, styles CANNOT be set via this property.
// Styles must be configured in Google Cloud Console for the mapId.
// We keep the mapId for AdvancedMarkerElement support and remove local styles.
export const getMapOptions = (isDarkMode: boolean): google.maps.MapOptions => ({
  mapId: '3cf64d0fcebdaf11f3625d6e', // CleanMap style - POIs disabled
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false, // Disable clicking on POI icons
  // Styles are configured in Google Cloud Console for the mapId
});

// Refresh interval for vehicle locations (in milliseconds)
export const REFRESH_INTERVAL = 10000; // 10 seconds

// Icon size multiplier settings
export const ICON_SIZE_CONFIG = {
  MIN: 0.5,
  MAX: 2,
  DEFAULT: 1,
  STEP: 0.1
};

// Vehicle status colors
export const VEHICLE_COLORS = {
  SELECTED: '#0073ae',      // Blue - Selected
  IN_PROGRESS: '#3B82F6',   // Blue - In Progress
  NOT_STARTED: '#F59E0B',   // Orange - Not Started
  DISPATCHED: '#6B7280',    // Grey - Dispatched
  AVAILABLE: '#10B981'      // Green - Available
};

// Default icon sizes by truck type
export const ICON_BASE_SIZES = {
  TruckEmpty: 32,
  TruckWithContainer: 60,
  TruckWithChassis: 60,
  Mule: 32
};

// Region coordinates for home region panning
// Maps region names (uppercase) to their center coordinates and default zoom level
export const REGION_COORDINATES: Record<string, { lat: number; lng: number; zoom: number }> = {
  'BALTIMORE': { lat: 39.2904, lng: -76.6122, zoom: 11 },
  'CHARLESTON': { lat: 32.7765, lng: -79.9311, zoom: 11 },
  'CHICAGO': { lat: 41.8781, lng: -87.6298, zoom: 10 },
  'DALLAS': { lat: 32.7767, lng: -96.7970, zoom: 10 },
  'HOUSTON': { lat: 29.7604, lng: -95.3698, zoom: 10 },
  'LA PORTE': { lat: 29.6658, lng: -95.0194, zoom: 12 },
  'MEMPHIS': { lat: 35.1495, lng: -90.0490, zoom: 11 },
  'MOBILE': { lat: 30.6954, lng: -88.0399, zoom: 11 },
  'NEW ORLEANS': { lat: 29.9511, lng: -90.0715, zoom: 11 },
  'NORFOLK': { lat: 36.8508, lng: -76.2859, zoom: 11 },
  'SAVANNAH': { lat: 32.0809, lng: -81.0912, zoom: 11 },
  'SEABROOK': { lat: 29.5641, lng: -95.0249, zoom: 12 },
};

// Get coordinates for a region, returns null if not found
export const getRegionCoordinates = (region: string | null | undefined): { lat: number; lng: number; zoom: number } | null => {
  if (!region) return null;
  return REGION_COORDINATES[region.toUpperCase()] || null;
};